// RetirementReadinessCalculator.js

import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Button,
  Slider,
  Paper,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
  Box,
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';
import { NumericFormat } from 'react-number-format';
import { v4 as uuidv4 } from 'uuid';
import { motion } from 'framer-motion';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import styled from 'styled-components';
import { styled as muiStyled } from '@mui/material/styles';
import { Card, CardContent } from '@mui/material';
import { alpha } from '@mui/material/styles';
import * as XLSX from 'xlsx';
import { Table as AntTable, ConfigProvider } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { blue, lightBlue, blueGrey, red, green } from '@mui/material/colors';
import { ColorPicker } from 'antd';

// Theme and styled components
const theme = createTheme({
  palette: {
    primary: {
      main: blue[700],
      light: blue[300],
      dark: blue[900],
    },
    secondary: {
      main: blueGrey[600],
      light: blueGrey[400],
      dark: blueGrey[800],
    },
    background: {
      default: lightBlue[50],
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: "'Poppins', sans-serif",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 30,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 15,
          },
        },
      },
    },
  },
});

const GlassCard = muiStyled(Card)(({ theme }) => ({
  background: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(10px)',
  borderRadius: 30,
  boxShadow: `0 8px 32px 0 ${alpha(theme.palette.primary.main, 0.2)}`,
  border: `1px solid ${alpha(theme.palette.primary.light, 0.18)}`,
  padding: theme.spacing(4),
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: `0 12px 40px 0 ${alpha(theme.palette.primary.main, 0.25)}`,
  },
}));

const ColorButton = muiStyled(Button)(({ theme, color }) => ({
  color: theme.palette.getContrastText(color),
  backgroundColor: color,
  '&:hover': {
    backgroundColor: alpha(color, 0.9),
  },
  borderRadius: 30,
  padding: '10px 20px',
  fontWeight: 600,
}));

const StyledTextField = styled.input`
  width: 100%;
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  font-size: 16px;
  font-family: 'Poppins', sans-serif;
  outline: none;
  transition: border-color 0.3s ease;
  
  &:focus {
    border-color: ${blue[500]};
  }
`;

const StyledSlider = styled(Slider)`
  color: ${blue[500]};
`;

const SectionSubtitle = styled(Typography)`
  font-size: 1.2rem;
  color: #4a4a4a;
  margin-bottom: 1rem;
  font-weight: 500;
`;

const StyledTableContainer = styled('div')`
  .ant-table {
    background: white;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }

  .ant-table-thead > tr > th {
    background: white !important;
    color: rgb(75, 85, 99) !important;
    font-weight: 500;
    padding: 16px;
    border-bottom: 1px solid #f0f0f0;
    
    &:hover {
      background: #fafafa !important;
    }
    
    &.ant-table-column-has-sorters:hover {
      background: #fafafa !important;
    }
  }

  .ant-table-tbody > tr > td {
    padding: 16px;
    border-bottom: 1px solid #f0f0f0;
  }

  .ant-table-tbody > tr:nth-of-type(even) {
    background-color: #fafafa;
  }

  .ant-table-tbody > tr:hover > td {
    background-color: #f5f5f5 !important;
  }

  @media (max-width: 767px) {
    .ant-table {
      font-size: 0.875rem;
    }
    
    .ant-table-thead > tr > th {
      padding: 8px 4px;
      white-space: nowrap;
    }
    
    .ant-table-tbody > tr > td {
      padding: 8px 4px;
    }

    .ant-space {
      gap: 4px !important;
    }
  }
`;

const RetirementReadinessCalculator = () => {
  // State variables
  const [scenarios, setScenarios] = useState([]);
  const [currentScenario, setCurrentScenario] = useState({
    id: uuidv4(),
    name: "Plan 1",
    avgParticipantBalance: 50000,
    avgContributionPercent: 6,
    employerMatch: 3,
    yearsToRetirement: 30,
    assumedReturnRate: 7,
    currentAge: 35,
    retirementAge: 65,
    annualSalary: 60000,
    includeInGraph: true,
    color: '#9b59b6',
  });

  // Retirement Readiness calculation functions
  const calculateRetirementReadiness = (scenario) => {
    const currentBalance = scenario.avgParticipantBalance;
    const annualContribution = scenario.annualSalary * (scenario.avgContributionPercent / 100);
    const employerMatchAmount = scenario.annualSalary * (scenario.employerMatch / 100);
    const totalAnnualContribution = annualContribution + employerMatchAmount;
    const returnRate = scenario.assumedReturnRate / 100;
    const yearsToRetirement = scenario.yearsToRetirement;
    
    // Calculate future value using compound interest formula
    const futureValueCurrentBalance = currentBalance * Math.pow(1 + returnRate, yearsToRetirement);
    const futureValueAnnuity = totalAnnualContribution * (Math.pow(1 + returnRate, yearsToRetirement) - 1) / returnRate;
    const projectedBalance = futureValueCurrentBalance + futureValueAnnuity;
    
    // Calculate replacement ratio needed (typically 70-80% of final salary)
    const finalSalary = scenario.annualSalary * Math.pow(1.03, yearsToRetirement); // 3% salary growth
    const neededRetirementIncome = finalSalary * 0.75; // 75% replacement ratio
    const neededRetirementBalance = neededRetirementIncome * 25; // 4% withdrawal rule
    
    // Calculate readiness metrics
    const readinessPercentage = (projectedBalance / neededRetirementBalance) * 100;
    const shortfall = Math.max(0, neededRetirementBalance - projectedBalance);
    const surplus = Math.max(0, projectedBalance - neededRetirementBalance);
    const onTrack = readinessPercentage >= 100;
    
    // Simulate participant distribution (simplified model)
    const participantDistribution = {
      onTrack: onTrack ? 65 : 35, // % of participants on track
      offTrack: onTrack ? 35 : 65, // % of participants off track
    };
    
    return {
      projectedBalance,
      neededRetirementBalance,
      readinessPercentage,
      shortfall,
      surplus,
      onTrack,
      participantDistribution,
      totalAnnualContribution,
      finalSalary,
      neededRetirementIncome,
    };
  };

  const generateRetirementReadinessData = () => {
    const scenariosToAnalyze = scenarios.length > 0 ? scenarios : [currentScenario];
    
    return scenariosToAnalyze.map(scenario => {
      const analysis = calculateRetirementReadiness(scenario);
      return {
        name: scenario.name,
        projectedBalance: analysis.projectedBalance,
        neededBalance: analysis.neededRetirementBalance,
        readinessPercentage: analysis.readinessPercentage,
        shortfall: analysis.shortfall,
        surplus: analysis.surplus,
        onTrack: analysis.onTrack,
        participantOnTrack: analysis.participantDistribution.onTrack,
        participantOffTrack: analysis.participantDistribution.offTrack,
        totalContribution: analysis.totalAnnualContribution,
        finalSalary: analysis.finalSalary,
        neededRetirementIncome: analysis.neededRetirementIncome,
        color: scenario.color,
      };
    });
  };

  // Scenario management functions
  const handleAddOrUpdateScenario = () => {
    const existingScenarioIndex = scenarios.findIndex(s => s.id === currentScenario.id);
    
    if (existingScenarioIndex !== -1) {
      const updatedScenarios = [...scenarios];
      updatedScenarios[existingScenarioIndex] = { ...currentScenario };
      setScenarios(updatedScenarios);
    } else {
      const newScenarioName = `Plan ${scenarios.length + 1}`;
      setScenarios([...scenarios, { ...currentScenario, name: newScenarioName }]);
    }

    setCurrentScenario({
      id: uuidv4(),
      name: `Plan ${scenarios.length + 2}`,
      avgParticipantBalance: 50000,
      avgContributionPercent: 6,
      employerMatch: 3,
      yearsToRetirement: 30,
      assumedReturnRate: 7,
      currentAge: 35,
      retirementAge: 65,
      annualSalary: 60000,
      includeInGraph: true,
      color: `hsl(${Math.random() * 360}, 70%, 50%)`,
    });
  };

  const handleEditScenario = (id) => {
    const scenarioToEdit = scenarios.find(scenario => scenario.id === id);
    setCurrentScenario(scenarioToEdit);
  };

  const handleRemoveScenario = (id) => {
    const updatedScenarios = scenarios.filter((scenario) => scenario.id !== id);
    const renamedScenarios = updatedScenarios.map((scenario, index) => ({
      ...scenario,
      name: `Plan ${index + 1}`
    }));
    setScenarios(renamedScenarios);
    
    setCurrentScenario(prev => ({
      ...prev,
      name: `Plan ${renamedScenarios.length + 1}`
    }));
  };

  // Format numbers with commas
  const formatNumber = (value) => {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Table state and handling
  const [sortedInfo, setSortedInfo] = useState({});
  const handleTableChange = (pagination, filters, sorter) => {
    setSortedInfo(sorter);
  };

  // Retirement Readiness table columns
  const columns = [
    {
      title: 'Plan Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortOrder: sortedInfo.columnKey === 'name' && sortedInfo.order,
    },
    {
      title: 'Projected Balance',
      dataIndex: 'projectedBalance',
      key: 'projectedBalance',
      sorter: (a, b) => (a.projectedBalance || 0) - (b.projectedBalance || 0),
      sortOrder: sortedInfo.columnKey === 'projectedBalance' && sortedInfo.order,
      render: (value) => `$${formatNumber(value.toFixed(2))}`,
    },
    {
      title: 'Needed Retirement Balance',
      dataIndex: 'neededBalance',
      key: 'neededBalance',
      sorter: (a, b) => (a.neededBalance || 0) - (b.neededBalance || 0),
      sortOrder: sortedInfo.columnKey === 'neededBalance' && sortedInfo.order,
      render: (value) => `$${formatNumber(value.toFixed(2))}`,
    },
    {
      title: 'Readiness %',
      dataIndex: 'readinessPercentage',
      key: 'readinessPercentage',
      sorter: (a, b) => (a.readinessPercentage || 0) - (b.readinessPercentage || 0),
      sortOrder: sortedInfo.columnKey === 'readinessPercentage' && sortedInfo.order,
      render: (value) => `${value.toFixed(1)}%`,
    },
    {
      title: 'Shortfall',
      dataIndex: 'shortfall',
      key: 'shortfall',
      sorter: (a, b) => (a.shortfall || 0) - (b.shortfall || 0),
      sortOrder: sortedInfo.columnKey === 'shortfall' && sortedInfo.order,
      render: (value) => `$${formatNumber(value.toFixed(2))}`,
    },
    {
      title: 'Surplus',
      dataIndex: 'surplus',
      key: 'surplus',
      sorter: (a, b) => (a.surplus || 0) - (b.surplus || 0),
      sortOrder: sortedInfo.columnKey === 'surplus' && sortedInfo.order,
      render: (value) => `$${formatNumber(value.toFixed(2))}`,
    },
    {
      title: 'On Track?',
      dataIndex: 'onTrack',
      key: 'onTrack',
      sorter: (a, b) => a.onTrack - b.onTrack,
      sortOrder: sortedInfo.columnKey === 'onTrack' && sortedInfo.order,
      render: (value) => (
        <span style={{ 
          color: value ? green[600] : red[600], 
          fontWeight: 'bold',
          padding: '4px 8px',
          borderRadius: '4px',
          backgroundColor: value ? green[50] : red[50]
        }}>
          {value ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      title: 'Participant On Track',
      dataIndex: 'participantOnTrack',
      key: 'participantOnTrack',
      sorter: (a, b) => (a.participantOnTrack || 0) - (b.participantOnTrack || 0),
      sortOrder: sortedInfo.columnKey === 'participantOnTrack' && sortedInfo.order,
      render: (value) => `${value}%`,
    },
    {
      title: 'Participant Off Track',
      dataIndex: 'participantOffTrack',
      key: 'participantOffTrack',
      sorter: (a, b) => (a.participantOffTrack || 0) - (b.participantOffTrack || 0),
      sortOrder: sortedInfo.columnKey === 'participantOffTrack' && sortedInfo.order,
      render: (value) => `${value}%`,
    },
    {
      title: 'Total Annual Contribution',
      dataIndex: 'totalContribution',
      key: 'totalContribution',
      sorter: (a, b) => (a.totalContribution || 0) - (b.totalContribution || 0),
      sortOrder: sortedInfo.columnKey === 'totalContribution' && sortedInfo.order,
      render: (value) => `$${formatNumber(value.toFixed(2))}`,
    },
    {
      title: 'Final Salary',
      dataIndex: 'finalSalary',
      key: 'finalSalary',
      sorter: (a, b) => (a.finalSalary || 0) - (b.finalSalary || 0),
      sortOrder: sortedInfo.columnKey === 'finalSalary' && sortedInfo.order,
      render: (value) => `$${formatNumber(value.toFixed(2))}`,
    },
    {
      title: 'Needed Retirement Income',
      dataIndex: 'neededRetirementIncome',
      key: 'neededRetirementIncome',
      sorter: (a, b) => (a.neededRetirementIncome || 0) - (b.neededRetirementIncome || 0),
      sortOrder: sortedInfo.columnKey === 'neededRetirementIncome' && sortedInfo.order,
      render: (value) => `$${formatNumber(value.toFixed(2))}`,
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 10
      }
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        style={{ padding: '24px' }}
      >
        <Grid container spacing={4}>
          {/* Input Section */}
          <Grid item xs={12} md={6}>
            <GlassCard variants={itemVariants}>
              <CardContent>
                <SectionSubtitle variant="h6" gutterBottom sx={{ color: theme.palette.secondary.main }}>
                  Define Retirement Readiness
                </SectionSubtitle>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Plan Name</Typography>
                  <StyledTextField
                    value={currentScenario.name}
                    onChange={(e) =>
                      setCurrentScenario({ ...currentScenario, name: e.target.value })
                    }
                    placeholder="Plan Name"
                  />
                </Box>
                <NumericFormat
                  customInput={StyledTextField}
                  value={currentScenario.avgParticipantBalance}
                  onValueChange={(values) =>
                    setCurrentScenario({ ...currentScenario, avgParticipantBalance: values.value })
                  }
                  thousandSeparator={true}
                  prefix={'$'}
                  placeholder="Average Participant Balance"
                  style={{ marginBottom: '16px' }}
                />
                <NumericFormat
                  customInput={StyledTextField}
                  value={currentScenario.annualSalary}
                  onValueChange={(values) =>
                    setCurrentScenario({ ...currentScenario, annualSalary: values.value })
                  }
                  thousandSeparator={true}
                  prefix={'$'}
                  placeholder="Annual Salary"
                  style={{ marginBottom: '16px' }}
                />
                <Typography gutterBottom>
                  Average Contribution %: {currentScenario.avgContributionPercent}%
                </Typography>
                <StyledSlider
                  value={currentScenario.avgContributionPercent}
                  onChange={(e, val) =>
                    setCurrentScenario({ ...currentScenario, avgContributionPercent: val })
                  }
                  aria-labelledby="avg-contribution-percent"
                  valueLabelDisplay="auto"
                  step={0.5}
                  min={0}
                  max={25}
                />
                <Typography gutterBottom>
                  Employer Match %: {currentScenario.employerMatch}%
                </Typography>
                <StyledSlider
                  value={currentScenario.employerMatch}
                  onChange={(e, val) =>
                    setCurrentScenario({ ...currentScenario, employerMatch: val })
                  }
                  aria-labelledby="employer-match"
                  valueLabelDisplay="auto"
                  step={0.25}
                  min={0}
                  max={10}
                />
                <Typography gutterBottom>
                  Years to Retirement: {currentScenario.yearsToRetirement}
                </Typography>
                <StyledSlider
                  value={currentScenario.yearsToRetirement}
                  onChange={(e, val) =>
                    setCurrentScenario({ ...currentScenario, yearsToRetirement: val })
                  }
                  aria-labelledby="years-to-retirement"
                  valueLabelDisplay="auto"
                  step={1}
                  min={1}
                  max={45}
                />
                <Typography gutterBottom>
                  Assumed Return Rate: {currentScenario.assumedReturnRate}%
                </Typography>
                <StyledSlider
                  value={currentScenario.assumedReturnRate}
                  onChange={(e, val) =>
                    setCurrentScenario({ ...currentScenario, assumedReturnRate: val })
                  }
                  aria-labelledby="assumed-return-rate"
                  valueLabelDisplay="auto"
                  step={0.5}
                  min={0}
                  max={15}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={currentScenario.includeInGraph}
                        onChange={(e) =>
                          setCurrentScenario({ ...currentScenario, includeInGraph: e.target.checked })
                        }
                        name="includeInGraph"
                        color="primary"
                      />
                    }
                    label="Include in Graph"
                  />
                  <ColorPicker
                    value={currentScenario.color}
                    onChange={(color) => setCurrentScenario({ ...currentScenario, color: color.toHexString() })}
                    trigger="hover"
                    disabledAlpha
                    presets={[
                      {
                        label: 'Recommended',
                        colors: [
                          '#4a90e2', '#50C878', '#FF6B6B', '#9B59B6',
                          '#34495E', '#2ECC71', '#E74C3C', '#8E44AD'
                        ],
                      },
                    ]}
                  />
                </Box>
                <Box sx={{ mt: 3 }}>
                  <ColorButton
                    variant="contained"
                    onClick={handleAddOrUpdateScenario}
                    startIcon={<AddIcon />}
                    color={currentScenario.color}
                    fullWidth
                  >
                    {scenarios.some(s => s.id === currentScenario.id) ? 'Update Plan' : 'Add Plan'}
                  </ColorButton>
                </Box>
              </CardContent>
            </GlassCard>
          </Grid>

          {/* Scenario List */}
          <Grid item xs={12} md={6}>
            <GlassCard variants={itemVariants}>
              <CardContent>
                <SectionSubtitle variant="h6" gutterBottom sx={{ color: theme.palette.secondary.main }}>
                  Retirement Plans
                </SectionSubtitle>
                {scenarios.length > 0 ? (
                  scenarios.map((scenario) => {
                    const analysis = calculateRetirementReadiness(scenario);
                    return (
                      <Box key={scenario.id} sx={{ mb: 2, position: 'relative' }}>
                        <Paper
                          elevation={3}
                          sx={{
                            p: 2,
                            borderRadius: '10px',
                            background: alpha(scenario.color, 0.1),
                            border: `1px solid ${alpha(scenario.color, 0.3)}`,
                          }}
                        >
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: scenario.color }}>
                            {scenario.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                            Projected Balance: <span style={{ fontWeight: 'bold' }}>
                              ${formatNumber(analysis.projectedBalance.toFixed(2))}
                            </span>
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Readiness: <span style={{ color: analysis.readinessPercentage >= 100 ? green[600] : red[600], fontWeight: 'bold' }}>
                              {analysis.readinessPercentage.toFixed(1)}%
                            </span>
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Participants On Track: <span style={{ color: analysis.participantDistribution.onTrack >= 50 ? green[600] : red[600], fontWeight: 'bold' }}>
                              {analysis.participantDistribution.onTrack}%
                            </span>
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={scenario.includeInGraph}
                                  onChange={(e) => {
                                    const newScenarios = scenarios.map((s) =>
                                      s.id === scenario.id ? { ...s, includeInGraph: e.target.checked } : s
                                    );
                                    setScenarios(newScenarios);
                                  }}
                                  name="includeInGraph"
                                  color="primary"
                                />
                              }
                              label="Include in Graph"
                            />
                            <Box>
                              <IconButton
                                color="primary"
                                onClick={() => handleEditScenario(scenario.id)}
                                size="small"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                color="secondary"
                                onClick={() => handleRemoveScenario(scenario.id)}
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </Box>
                        </Paper>
                      </Box>
                    );
                  })
                ) : (
                  <Typography variant="body1">No saved plans. Create one to get started!</Typography>
                )}
              </CardContent>
            </GlassCard>
          </Grid>

          {/* Retirement Readiness Comparison Chart */}
          <Grid item xs={12} md={6}>
            <GlassCard variants={itemVariants}>
              <CardContent>
                <SectionSubtitle variant="h6" gutterBottom sx={{ color: theme.palette.secondary.main }}>
                  Projected vs Needed Balance
                </SectionSubtitle>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={generateRetirementReadinessData()}>
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip 
                      formatter={(value, name) => {
                        if (name === 'projectedBalance' || name === 'neededBalance') {
                          return [`$${formatNumber(value.toFixed(2))}`, name];
                        }
                        return [value, name];
                      }}
                    />
                    <Legend />
                    <Bar dataKey="projectedBalance" fill="#4a90e2" name="Projected Balance" />
                    <Bar dataKey="neededBalance" fill="#ff6b6b" name="Needed Balance" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </GlassCard>
          </Grid>

          {/* Participant Distribution Pie Chart */}
          <Grid item xs={12} md={6}>
            <GlassCard variants={itemVariants}>
              <CardContent>
                <SectionSubtitle variant="h6" gutterBottom sx={{ color: theme.palette.secondary.main }}>
                  Participant Distribution
                </SectionSubtitle>
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={generateRetirementReadinessData().length > 0 ? [
                        { name: 'On Track', value: generateRetirementReadinessData()[0].participantOnTrack, fill: green[500] },
                        { name: 'Off Track', value: generateRetirementReadinessData()[0].participantOffTrack, fill: red[500] }
                      ] : []}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      <Cell fill={green[500]} />
                      <Cell fill={red[500]} />
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </GlassCard>
          </Grid>

          {/* Data Table */}
          <Grid item xs={12}>
            <GlassCard variants={itemVariants} style={{ marginBottom: '2rem' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <SectionSubtitle variant="h6" sx={{ color: theme.palette.secondary.main }}>
                    Retirement Readiness Analysis
                  </SectionSubtitle>
                  <Tooltip title="Download Excel">
                    <Button
                      onClick={() => {
                        const worksheet = XLSX.utils.json_to_sheet(generateRetirementReadinessData());
                        const workbook = XLSX.utils.book_new();
                        XLSX.utils.book_append_sheet(workbook, worksheet, "Retirement Readiness Analysis");
                        XLSX.writeFile(workbook, "retirement_readiness_analysis.xlsx");
                      }}
                      startIcon={<DownloadOutlined />}
                      sx={{
                        color: theme.palette.secondary.main,
                        '&:hover': {
                          color: theme.palette.primary.main,
                          backgroundColor: 'transparent',
                        },
                        transition: 'all 0.3s ease',
                        textTransform: 'none',
                        fontSize: '14px',
                      }}
                    >
                      Download Excel
                    </Button>
                  </Tooltip>
                </Box>
                <ConfigProvider
                  theme={{
                    token: {
                      fontFamily: "'Poppins', sans-serif",
                      borderRadius: 12,
                      colorBgContainer: '#ffffff',
                      colorBorderSecondary: '#f0f0f0',
                      fontSize: 14,
                    },
                    components: {
                      Table: {
                        headerBg: '#ffffff',
                        headerColor: 'rgb(75, 85, 99)',
                        rowHoverBg: '#f5f5f5',
                        headerSortHoverBg: '#fafafa',
                        headerSortActiveBg: '#fafafa',
                        borderColor: '#f0f0f0',
                        rowExpandedBg: '#fafafa',
                      },
                    },
                  }}
                >
                  <StyledTableContainer>
                    <AntTable
                      columns={columns}
                      dataSource={generateRetirementReadinessData()}
                      rowKey="name"
                      onChange={handleTableChange}
                      pagination={{ 
                        pageSize: 10,
                        showSizeChanger: false,
                        showQuickJumper: true,
                        style: { marginTop: '16px' }
                      }}
                      scroll={{ x: 'max-content' }}
                    />
                  </StyledTableContainer>
                </ConfigProvider>
              </CardContent>
            </GlassCard>
          </Grid>
        </Grid>
      </motion.div>
    </ThemeProvider>
  );
};

export default RetirementReadinessCalculator; 